/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
/* eslint-disable react/no-danger */

import React, { ReactElement, useCallback } from 'react';
import Button from '@userzoomlibraries/toolkit-button';
import StudyLayout, { useDevice } from '@userzoomplatform/pe-study-layout';
import { useTranslation } from 'react-i18next';
import Input from '@userzoomlibraries/toolkit-input';
import { QuestionError, QuestionTitle } from '@userzoomplatform/pe-study-tasks';

import { useFormik } from 'formik';
import { gql, useMutation } from '@apollo/client';
import { usePageView } from '../../../../hooks';
import { Namespaces } from '../../../../utils/i18n';
import { closeIframe } from '../../../../utils/post-messages';
import { inIframe } from '../../../../utils/iframe';
import { AnswerAccessAgreementOutcome } from '../../../../types/pre-study';

const answerAccessAgreementMutation = gql`
	mutation answerAccessAgreement($input: AnswerAccessAgreementInput!) {
		answerAccessAgreement(input: $input) {
			success
			redirectUrl
			attemptsLeft
		}
	}
`;

type Props = {
	moveNext?: () => void;
	label: string;
	instructions: string;
	className?: string;
	isModerated?: boolean;
};

const defaultProps = {
	moveNext: () => null,
	className: '',
	isModerated: false,
};

const { Desktop, Common } = Namespaces;

const AccessAgreementPage = ({ moveNext, label, instructions, className, isModerated }: Props): ReactElement => {
	const { t } = useTranslation([Common, Desktop]);
	const { device } = useDevice();

	usePageView('/access-agreement-page');

	const handleValidate = useCallback(({ accessCode }: { accessCode: string }) => {
		let errors = {};
		if (!accessCode) {
			errors = { accessCode: 'Please provide a response in the empty fields' };
		}
		return errors;
	}, []);

	const { values, touched, setFieldValue, handleBlur, errors, handleSubmit, setFieldError } = useFormik({
		initialValues: {
			accessCode: '',
		},
		validate: handleValidate,
		onSubmit: ({ accessCode }) => {
			answerAccessAgreement({
				variables: { input: { accessCode } },
			});
		},
	});

	const [answerAccessAgreement] = useMutation<AnswerAccessAgreementOutcome>(answerAccessAgreementMutation, {
		onCompleted(response: AnswerAccessAgreementOutcome) {
			if (isModerated) {
				window.location.href = response.answerAccessAgreement?.redirectUrl;
			}
			if (response.answerAccessAgreement?.success) {
				if (moveNext) {
					moveNext();
				}
			} else if (response.answerAccessAgreement?.attemptsLeft === 1) {
				setFieldError('accessCode', t(`${Desktop}:accessAgreement.oneAttemptLeft`));
			} else if (response.answerAccessAgreement?.attemptsLeft === 0) {
				window.location.href = response.answerAccessAgreement?.redirectUrl;
			} else {
				setFieldError('accessCode', t(`${Desktop}:accessAgreement.incorrectAnswer`));
			}
		},
	});

	return (
		<StudyLayout className={className}>
			<StudyLayout.Header exitText={inIframe ? t(`${Namespaces.Common}:cta.quit`) : ''} onExitClick={closeIframe} />
			<StudyLayout.Content>
				<div className="questions-wrapper">
					<QuestionTitle text={instructions} mandatory device={device} />
					<div className="content">
						<div className="instructions" dangerouslySetInnerHTML={{ __html: label }} />
						<Input
							name={t(`${Desktop}:accessAgreement.accessCode`)}
							type="text"
							placeholder={t(`${Desktop}:accessAgreement.placeholder`)}
							onChange={(e: { value: string }) => setFieldValue('accessCode', e.value)}
							value={values.accessCode}
							onBlur={handleBlur}
						/>
						{touched.accessCode && errors.accessCode && (
							<QuestionError>
								<span className="reason">{errors.accessCode ?? ''}</span>
							</QuestionError>
						)}
					</div>
				</div>
			</StudyLayout.Content>
			<StudyLayout.Footer>
				<Button onClick={handleSubmit}>{t(`${Namespaces.Common}:cta.startStudy`)}</Button>
			</StudyLayout.Footer>
			{!inIframe && <StudyLayout.FooterText />}
		</StudyLayout>
	);
};

AccessAgreementPage.defaultProps = defaultProps;

export default AccessAgreementPage;
